import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Single step in email scraping pipeline.
 */
export const EmailScrapePipelineStepSchema = z
  .object({
    ordinal: z.number().int().min(0).describe('Execution order (0-indexed). Steps run in ascending order.').optional(),
    plugin: z
      .enum(['trim', 'getMetaContent', 'matchThenNextElement', 'extractBetween'])
      .describe(
        'Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers'
      )
      .optional(),
    params: z.record(z.string()).describe('Plugin-specific parameters as key-value pairs.').optional(),
  })
  .describe('Single step in email scraping pipeline.');

export type EmailScrapePipelineStep = z.infer<typeof EmailScrapePipelineStepSchema>;

/**
 * Field mapping for extracting data from emails.
 */
export const EmailScrapeMappingSchema = z
  .object({
    selector: z.string().describe('CSS selector to locate the element containing the field value.').optional(),
    pipeline: z
      .array(EmailScrapePipelineStepSchema)
      .describe('Ordered processing steps to extract and transform the value.')
      .optional(),
  })
  .strict()
  .describe('Field mapping for extracting data from emails.');

export type EmailScrapeMapping = z.infer<typeof EmailScrapeMappingSchema>;

/**
 * Email scraping configuration for automated data extraction.
 */
export const schema = z
  .object({
    name: z.string().describe('Configuration name for identification.').optional(),
    version: z.string().describe('Schema version for compatibility tracking.').optional(),
    key: z.string().describe('Unique key identifier for this configuration.').optional(),
    mappings: z
      .object({
        name: EmailScrapeMappingSchema.optional(),
        phone: EmailScrapeMappingSchema.optional(),
        address: EmailScrapeMappingSchema.optional(),
        vehicle: EmailScrapeMappingSchema.optional(),
        vin: EmailScrapeMappingSchema.optional(),
        advisor: EmailScrapeMappingSchema.optional(),
        services: EmailScrapeMappingSchema.optional(),
        dealer_code: EmailScrapeMappingSchema.optional(),
        appointment_time: EmailScrapeMappingSchema.optional(),
        appointment_type: EmailScrapeMappingSchema.optional(),
        arrangement: EmailScrapeMappingSchema.optional(),
        confirmation_code: EmailScrapeMappingSchema.optional(),
      })
      .strict()
      .describe('Field extraction mappings for appointment email data.')
      .optional(),
  })
  .strict()
  .describe('Email scraping configuration for automated data extraction from appointment emails.');

export type EmailScrapeConfig = z.infer<typeof schema>;

export const definition = define({
  key: 'email_scrape_config',
  schema,
  label: 'Email Scrape Config',
  description: 'Email scraping configuration for automated data extraction.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.EMAIL_SCRAPING.key,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
});
